import { matchesUiText } from '../i18n';
// src/utils/previewGenerator.ts
import { ASTNode, PreviewMeta, FileAttachment } from '../types/preview';
import { PreviewErrorHandler } from './previewErrorHandler';

const generatePreview = (ast: ASTNode[], maxLength: number = 1000): PreviewMeta => {
  let previewText = '';
  let imageCount = 0;
  let attachmentCount = 0;
  const attachmentTypes = new Set<string>();
  let firstHeading = '';
  let hasLinks = false;
  let hasInternalLinks = false;
  let internalLinkCount = 0;
  let hasExternalLinks = false;
  let externalLinkCount = 0;
  let firstImageThumb = '';

  // fileattachmentタグの抽出（正規表現）
  const extractFileAttachments = (text: string): FileAttachment[] => {
    const fileAttachmentRegex = /<fileattachment[^>]*title="([^"]*)"[^>]*mimeType="([^"]*)"[^>]*>/g;
    const attachments: FileAttachment[] = [];
    let match;
    
    while ((match = fileAttachmentRegex.exec(text)) !== null) {
      attachments.push({
        title: match[1],
        mimeType: match[2]
      });
    }
    
    return attachments;
  };

  // 安全な文字列切り詰め（マルチバイト対応）
  const safeTruncate = (text: string, maxLength: number): string => {
    const chars = Array.from(text);
    if (chars.length <= maxLength) return text;
    
    return chars.slice(0, maxLength).join('') + '...';
  };

  // セキュリティ強化されたテキスト処理
  const sanitizeText = (text: string): string => {
    return text
      .replace(/<fileattachment[^>]*>.*?<\/fileattachment>/g, '') // タグ除去
      .replace(/https?:\/\/[^\s]+token=[^\s&]+/g, '') // トークン付きURL除去
      .replace(/storage\.googleapis\.com[^\s]*/g, '') // Storage URL除去
      .replace(/\n+/g, '\n') // 連続改行を単一に
      .trim();
  };

  // 安全なサムネイルURL生成
  const generateSafeThumbnailUrl = (originalUrl: string): string => {
    // プロキシ経由（推奨）
    if (import.meta.env.VITE_THUMBNAIL_PROXY_URL) {
      return `${import.meta.env.VITE_THUMBNAIL_PROXY_URL}?url=${encodeURIComponent(originalUrl)}`;
    }
    
    // フォールバック：公開サムネイル用パスに変換
    return originalUrl.replace(/\/o\//, '/o/thumbnails/').replace(/\?.*$/, '');
  };

  // URL検出用の正規表現
  const urlPattern = /https?:\/\/[^\s]+/g;

  // テキスト内のURLを検出して外部リンクとしてカウント
  const detectURLsInText = (text: string): string => {
    const urls = text.match(urlPattern);
    if (urls) {
      urls.forEach(() => {
        hasLinks = true;
        hasExternalLinks = true;
        externalLinkCount++;
      });
    }
    return text;
  };

  // ノード処理関数
  const processNode = (node: ASTNode, isLabelCandidate = false): string => {
    switch (node.type) {
      case 'paragraph':
        if (node.content) {
          const text = node.content.map(childNode => processNode(childNode)).join('');
          
          // ラベル行の判定（直後のノード種別で判定）
          if (isLabelCandidate && (matchesUiText(text, "Image") || matchesUiText(text, "File"))) {
            return ''; // ラベル行は除外
          }
          
          return text;
        }
        
        // textノード内のfileattachmentを処理
        if (node.text) {
          const attachments = extractFileAttachments(node.text);
          attachments.forEach(att => {
            attachmentCount++;
            if (att.mimeType) {
              attachmentTypes.add(att.mimeType.split('/')[1] || 'unknown');
            }
          });
          
          // fileattachmentタグを除去してテキストを返す
          return sanitizeText(node.text);
        }
        
        return '';
        
      case 'heading':
        const headingText = node.content?.map(childNode => processNode(childNode)).join('') || '';
        if (!firstHeading) firstHeading = headingText;
        return headingText;
        
      case 'text':
        const textContent = node.text || '';
        // テキスト内のURLを検出
        detectURLsInText(textContent);
        return sanitizeText(textContent);
        
      case 'autoLink':
        // 内部リンクのテキストを含める
        if (node.attrs?.linkText) {
          hasLinks = true;  // 全体のリンクフラグ
          hasInternalLinks = true;  // 内部リンクフラグ
          internalLinkCount++;  // 内部リンクカウント
          return node.attrs.linkText;  // リンクテキストを返す
        }
        return '';
        
      case 'image':
        imageCount++;
        if (!firstImageThumb && node.attrs?.src) {
          firstImageThumb = generateSafeThumbnailUrl(node.attrs.src);
        }
        return ''; // 画像は本文から除外
        
      case 'fileAttachment':
        // ファイル添付ノードをメタデータとして処理
        attachmentCount++;
        if (node.attrs?.mimeType) {
          attachmentTypes.add(node.attrs.mimeType.split('/')[1] || 'unknown');
        }
        return ''; // ファイル添付は本文から除外
        
      case 'hard_break':
        return '\n';
        
      case 'bullet_list':
      case 'ordered_list':
        return node.content?.map(childNode => processNode(childNode)).join('') || '';
        
      case 'list_item':
        return node.content?.map(childNode => processNode(childNode)).join('') || '';
        
      case 'link':
        hasLinks = true;  // 全体のリンクフラグ
        hasExternalLinks = true;  // 外部リンクフラグ
        externalLinkCount++;  // 外部リンクカウント
        return node.content?.map(childNode => processNode(childNode)).join('') || '';
        
      // 表はカード内の抜粋として読める形にする（行=改行、セル= / 区切り）
      case 'table':
        return node.content?.map(childNode => processNode(childNode)).filter(text => text.trim()).join('\n') || '';
        
      case 'tableRow':
        return node.content?.map(childNode => processNode(childNode)).filter(text => text.trim()).join(' / ') || '';
        
      case 'tableCell':
      case 'tableHeader':
        return node.content?.map(childNode => processNode(childNode)).join(' ').trim() || '';
        
      default:
        // その他の特殊ノードは除外
        if (node.content) {
          return node.content.map(childNode => processNode(childNode)).join('');
        }
        return '';
    }
  };

  // ASTを順次処理（ラベル行判定のため）
  for (let i = 0; i < ast.length; i++) {
    const node = ast[i];
    const nextNode = ast[i + 1];
    
    // ラベル行判定：現在がparagraphで、次がimageまたはfileattachment
    const isLabelCandidate = node.type === 'paragraph' && 
      (nextNode?.type === 'image' || 
       nextNode?.type === 'fileAttachment' ||
       (nextNode?.type === 'text' && typeof nextNode.text === 'string' && nextNode.text.includes('<fileattachment')));
    
    const text = processNode(node, isLabelCandidate);
    if (text) {
      previewText += text + '\n';
    }
  }

  // 最終的なテキスト処理
  previewText = sanitizeText(previewText);
  previewText = safeTruncate(previewText, maxLength);

  const result = {
    previewText,
    hasImages: imageCount > 0,
    imageCount,
    firstImageThumb,
    hasAttachments: attachmentCount > 0,
    attachmentCount,
    attachmentTypes: Array.from(attachmentTypes),
    hasLinks,
    hasInternalLinks,
    internalLinkCount,
    hasExternalLinks,
    externalLinkCount,
    firstHeading
  };

  return result;
};

/**
 * エラーハンドリング付きプレビュー生成関数（推奨）
 */
export const generatePreviewSafe = async (
  ast: ASTNode[], 
  maxLength: number = 200,
  context?: string
): Promise<PreviewMeta | null> => {
  return PreviewErrorHandler.safeGeneratePreview(
    () => generatePreview(ast, maxLength),
    { previewText: '', hasImages: false, imageCount: 0, hasAttachments: false, attachmentCount: 0, attachmentTypes: [], hasLinks: false },
    context
  );
};

export { generatePreview };
